import { useMsal } from '@azure/msal-react';
import { useMemo } from 'react';
import { loginRequest } from './msalConfig';
import { clearIdleLock } from './idleLockState';

const ROLE_CLAIM = import.meta.env.VITE_ROLE_CLAIM || 'roles';

/**
 * Normalised auth helper. Returns:
 *   - account: active MSAL account (or null)
 *   - name, email, initials: convenience fields
 *   - roles: array of role strings from the ID token
 *   - isDirector, isRegionalManager, isLiaisonManager, isLiaison: booleans
 *   - hasRole(role): membership check
 *   - login(), logout(): thin wrappers
 */
export default function useAuth() {
  const { instance, accounts } = useMsal();
  const account = accounts[0] || instance.getActiveAccount() || null;

  const roles = useMemo(() => {
    if (!account) return [];
    const claims = account.idTokenClaims || {};
    const raw = claims[ROLE_CLAIM];
    if (!raw) return [];
    return Array.isArray(raw) ? raw : [raw];
  }, [account]);

  const name = account?.name || account?.username || '';
  const email = account?.username || '';
  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

  const hasRole = (role) => roles.includes(role);

  const login = () => instance.loginRedirect(loginRequest);
  const logout = () => {
    // A signed-out user must not inherit the idle lock on their next
    // sign-in. Leaving this flag behind caused an endless "sign in →
    // instantly locked again" loop (July 2026).
    clearIdleLock();
    return instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  };

  return {
    account,
    name,
    email,
    initials,
    roles,
    isDirector: hasRole('Director'),
    isExecutive: hasRole('Executive'),
    isRegionalManager: hasRole('RegionalManager'),
    isLiaisonManager: hasRole('LiaisonManager'),
    isLiaison: hasRole('Liaison'),
    hasRole,
    login,
    logout,
  };
}
